import React, { useEffect } from "react";

import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import { colors } from "@zapalacorp/tokens";
import Check from "Components/Generic/Icons/Check";
import {
  EquipmentIcons,
  getFleetIconEquipmentNames,
  getGeneralIconEquipmentNames,
} from "Components/Generic/Icons/Equipment";
import { UploadImageFileInput } from "Components/Generic/UploadImage";
import { isBefore } from "date-fns";
import { Picture } from "Models/picture";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import "@fontsource/manrope";
import { AutoSuggest, Input, Select } from "../../../../../../../../../components/Form";
import Checkbox from "../../../../../../../../../components/Form/Checkbox";
import { TooltipPlacements } from "../../../../../../../../../components/Form/ToolTipInfo";
import SkeletonComponent from "../../../../../../../../../components/Generic/Skeleton";
import IconWrapper from "../../../../../../../../../components/IconWrapper";
import useYupValidation from "../../../../../../../../../hooks/useYupValidation";
import { Branch } from "../../../../../../../../../models/branch";
import { Fleet } from "../../../../../../../../../models/fleet";
import { Warehouse } from "../../../../../../../../../models/warehouse";
import { EquipmentOwnership, EquipmentType, Status } from "../../../../../../../../../util/enums";
import { formatCurrencyToInput } from "../../../../../../../../../util/formatCurrencyToInput";
import { getMeasurement } from "../../../../../../../../../util/getMeasurement";
import { currencyToNumber } from "../../../../../../../../../util/prices";
import useEquipmentActions from "../../../hooks/useEquipmentActions";
import { useEquipmentContext } from "../../context/Equipment";
import {
  BranchOption,
  BranchOptionText,
  CheckedBox,
  DetailsContainer,
  IconBox,
  IconBoxContent,
  IconsContainer,
  LeftContentContainer,
  RightContentContainer,
  RightContentWrapper,
  Title,
} from "./styles";
import { SelectChangeEvent } from "@mui/material";

let temporaryBranchesSelected: Branch[] = [];

type Props = {
  errors: yup.ValidationError[];
  isView: boolean;
};

export const Details: React.FC<Props> = ({ errors, isView }) => {
  const { t: translate } = useTranslation();

  const { equipment: fields, setEquipment, onUpdateEquipment } = useEquipmentContext();
  const {
    getAllBranches,
    getFleetTypes,
    getAllWarehouses,
    getAllTeams,
    getAllSubcontractors,
    getFleetTypeModes,
    getOwnerhips,
  } = useEquipmentActions();
  const { data: warehouseList, isLoading: loadingWarehouses } = getAllWarehouses();
  const { data: branchList, isLoading: loadingBranches } = getAllBranches();
  const { data: fleetTypes, isLoading: loadingFleetTypes } = getFleetTypes();
  const { data: teamList, isLoading: loadingTeams } = getAllTeams();
  const { data: subcontractorList, isLoading: loadingSubcontractors } = getAllSubcontractors();
  const fleetTypeModeList = getFleetTypeModes();
  const ownershipOptions = getOwnerhips();

  const fleetTypeEquipmentIconsNames = getFleetIconEquipmentNames();
  const generalTypeEquipmentIconsNames = getGeneralIconEquipmentNames();

  const onChangeField = (key: keyof Fleet, event: React.ChangeEvent<any>) => {
    if (event && event.target) {
      if (key === "ownership" && event.target.value !== EquipmentOwnership.SUBCONTRACTOR) {
        onUpdateEquipment(key, event.target.value);
        onUpdateEquipment("subcontractorId", null);
        onUpdateEquipment("subcontractor", null);
      }
      onUpdateEquipment(key, event.target.value);
    }
  };

  const onSetAllField = (value: any) => {
    setEquipment((prev) => ({ ...prev, ...value }));
  };

  const onSetField = (key: keyof Fleet, value: any) => {
    onUpdateEquipment(key, value);
  };

  const { getErrorByField } = useYupValidation<Fleet>(errors);

  const onChangePicture = (picture?: string) => {
    setEquipment((prev) => ({
      ...prev,
      picture: { mediaLink: picture } as Picture,
      pictureId: null,
    }));
  };

  const renderLeftContentByEquipmentTypeSelected = () => {
    return (
      <LeftContentContainer>
        <UploadImageFileInput
          labelText={translate("equipmentPhoto")}
          onChangePicture={(value) => {
            onChangePicture(value);
          }}
          picture={fields.picture?.mediaLink}
          onRemovePicture={() => {
            setEquipment((prev) => ({ ...prev, picture: undefined }));
          }}
          containerGap="32px"
        />
        <IconsContainer>
          <Title>{translate("equipmentIcon")}</Title>
          {fields.equipmentType === EquipmentType.FLEET && (
            <IconBoxContent>
              {fleetTypeEquipmentIconsNames.map((name) => {
                const selected = fields.iconName === name;

                return (
                  <IconBox
                    key={name}
                    selected={selected}
                    onClick={() => onSetField("iconName", name)}
                  >
                    {selected && (
                      <CheckedBox>
                        <Check color={colors.white} width={16} height={16} />
                      </CheckedBox>
                    )}
                    <EquipmentIcons
                      name={name}
                      color={colors.gray600}
                      size={32}
                      typeOfEquipment={EquipmentType.FLEET}
                    />
                  </IconBox>
                );
              })}
            </IconBoxContent>
          )}
          {fields.equipmentType === EquipmentType.GENERAL && (
            <IconBoxContent>
              {generalTypeEquipmentIconsNames.map((name) => {
                const selected = fields.iconName === name;

                return (
                  <IconBox
                    key={name}
                    selected={selected}
                    onClick={() => onSetField("iconName", name)}
                    disabled={isView}
                  >
                    {selected && (
                      <CheckedBox>
                        <Check color={colors.white} width={16} height={16} />
                      </CheckedBox>
                    )}
                    <EquipmentIcons
                      name={name}
                      color={colors.gray600}
                      size={32}
                      typeOfEquipment={EquipmentType.GENERAL}
                    />
                  </IconBox>
                );
              })}
            </IconBoxContent>
          )}
        </IconsContainer>
      </LeftContentContainer>
    );
  };

  const renderDetailsContentByEquipmentTypeSelected = () => {
    if (fields?.equipmentType === EquipmentType.FLEET) {
      const isTruckSelected = fleetTypes?.find(
        (type) => type?.id === fields?.fleetTypeId && type?.name?.toLowerCase().includes("truck")
      );

      return (
        <>
          <Title>{translate("generalDetails")}</Title>
          <RightContentContainer>
            <RightContentWrapper item xs={9} container direction="row" spacing={3}>
              <Grid item xs={4}>
                <SkeletonComponent isLoading={loadingFleetTypes}>
                  <Select
                    label={translate("type")}
                    value={fields.fleetTypeId}
                    onChange={(event: any) => onChangeField("fleetTypeId", event)}
                    required
                    error={!!getErrorByField("fleetTypeId")}
                    errorMessage={getErrorByField("fleetTypeId")}
                    disabled={isView}
                  >
                    {(fleetTypes || []).map((type, index) => (
                      <MenuItem value={type.id} key={index}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </Select>
                </SkeletonComponent>
              </Grid>
              {isTruckSelected && (
                <Grid item xs={4}>
                  <Select
                    data-cy="fleetModeType"
                    name="fleetModeType"
                    label={translate("typeMode")}
                    required
                    value={fields?.typeMode}
                    onChange={(event: any) => onChangeField("typeMode", event)}
                    error={!!getErrorByField("typeMode")}
                    errorMessage={getErrorByField("typeMode")}
                    disabled={isView}
                  >
                    {fleetTypeModeList.map((modeType) => (
                      <MenuItem
                        data-cy={modeType.value}
                        value={modeType.value}
                        key={modeType.value}
                      >
                        {modeType.label}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
              )}
              <Grid item xs={4}>
                <Select
                  label={translate("status")}
                  value={fields.status}
                  onChange={(event: any) => onChangeField("status", event)}
                  disabled={isView}
                >
                  {[Status.ACTIVE, Status.INACTIVE].map((option, index) => (
                    <MenuItem value={option} key={index}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              {!isTruckSelected && <Grid item xs={4}></Grid>}
              <Grid item xs={4}>
                <Input
                  label={translate("description")}
                  value={fields.description}
                  onChange={(event) => onChangeField("description", event)}
                  disabled={isView}
                  required
                  error={!!getErrorByField("description")}
                  errorMessage={getErrorByField("description")}
                />
              </Grid>
              <Grid item xs={4}>
                <Input
                  label={translate("nickname")}
                  value={fields.nickname}
                  onChange={(event) => onChangeField("nickname", event)}
                  disabled={isView}
                />
              </Grid>
              <Grid item xs={4}>
                <Input
                  label={translate("fleetNumber")}
                  value={fields.fleetNumber}
                  onChange={(event) => onChangeField("fleetNumber", event)}
                  required
                  error={!!getErrorByField("fleetNumber")}
                  errorMessage={getErrorByField("fleetNumber")}
                  disabled={isView}
                />
              </Grid>
              <Grid item xs={4}>
                <Input
                  label={translate("vinNumber")}
                  value={fields.vinNumber}
                  onChange={(event) => onChangeField("vinNumber", event)}
                  disabled={isView}
                />
              </Grid>
              <Grid item xs={4}>
                <Input
                  label={translate("make")}
                  value={fields.make}
                  onChange={(event) => onChangeField("make", event)}
                  disabled={isView}
                />
              </Grid>
              <Grid item xs={4}>
                <Input
                  label={translate("model")}
                  value={fields.model}
                  onChange={(event) => onChangeField("model", event)}
                  disabled={isView}
                />
              </Grid>
              <Grid item xs={4}>
                <Input
                  label={translate("year")}
                  value={fields.year}
                  onChange={(event) => onChangeField("year", event)}
                  disabled={isView}
                />
              </Grid>
              <Grid item xs={4}>
                <Input
                  label={translate("engineType")}
                  value={fields.engine}
                  onChange={(event) => onChangeField("engine", event)}
                  disabled={isView}
                />
              </Grid>
              <Grid item xs={4}>
                <Input
                  label={translate("plateNumber")}
                  value={fields.licensePlate}
                  onChange={(event) => onChangeField("licensePlate", event)}
                  disabled={isView}
                />
              </Grid>
              <Grid item xs={4}>
                <Input
                  label={translate("fuelType")}
                  value={fields.fuelType}
                  onChange={(event) => onChangeField("fuelType", event)}
                  disabled={isView}
                />
              </Grid>
              <Grid item xs={4}>
                <Input
                  label={translate("condition")}
                  value={fields.condition}
                  onChange={(event) => onChangeField("condition", event)}
                  disabled={isView}
                />
              </Grid>
              <Grid item xs={4}>
                <Input
                  label={translate("capacity")}
                  value={fields.capacityM3}
                  onChange={(event) => onChangeField("capacityM3", event)}
                  rightIcon={<span>{getMeasurement()?.code}</span>}
                  disabled={isView}
                />
              </Grid>
              <Grid item xs={4}>
                <Input
                  label={translate("euronorm")}
                  value={fields.euronorm}
                  onChange={(event) => onChangeField("euronorm", event)}
                  disabled={isView}
                />
              </Grid>
              <Grid item xs={4}>
                <Input
                  label={translate("driverLicense")}
                  value={fields.driverLicense}
                  onChange={(event) => onChangeField("driverLicense", event)}
                  disabled={isView}
                />
              </Grid>
              <Grid item xs={4}>
                <Input
                  label={translate("manufacturer")}
                  value={fields.manufacturer}
                  onChange={(event) => onChangeField("manufacturer", event)}
                  disabled={isView}
                />
              </Grid>
              <Grid item xs={4}>
                <AutoSuggest
                  disableCloseOnSelect
                  label={translate("branch")}
                  placeholder={fields?.branches?.length ? "" : translate("select")}
                  ignoreValue
                  required
                  disabled={isView}
                  onOpen={() => {
                    temporaryBranchesSelected = fields?.branches || [];
                  }}
                  onChange={(branches: Branch[]) => {
                    temporaryBranchesSelected = branches;
                    onSetField("branches", temporaryBranchesSelected);
                  }}
                  onClose={() => {
                    onSetField("branches", temporaryBranchesSelected);
                    temporaryBranchesSelected = [];
                  }}
                  defaultValue={fields?.branches || []}
                  error={!!getErrorByField("branches")}
                  errorMessage={getErrorByField("branches")}
                  multiple
                  options={branchList || []}
                  getOptionLabel={(branch?: Branch) => (branch && branch.name ? branch.name : "")}
                  getOptionSelected={(option: Branch, value: Branch) => {
                    return option.id === value.id;
                  }}
                  renderOption={(
                    props: React.HTMLAttributes<any>,
                    branch: Branch,
                    { selected }: { selected: boolean }
                  ) => (
                    <BranchOption {...props}>
                      <Checkbox checked={selected} />
                      <BranchOptionText>{branch.name}</BranchOptionText>
                    </BranchOption>
                  )}
                />
              </Grid>
              <Grid item xs={4}>
                <SkeletonComponent isLoading={loadingWarehouses}>
                  <AutoSuggest
                    dataCy="warehouseAutosuggest"
                    label={translate("warehouse")}
                    required
                    disabled={isView}
                    placeholder={translate("searchForWarehouse")}
                    error={!!getErrorByField("warehouseId")}
                    errorMessage={getErrorByField("warehouseId")}
                    inputValue={fields?.warehouse?.name || ""}
                    getOptionLabel={(warehouse?: Warehouse) =>
                      warehouse && warehouse.name ? warehouse.name : ""
                    }
                    getOptionSelected={(option?: Warehouse, value?: Warehouse) =>
                      option?.id === value?.id
                    }
                    options={warehouseList || []}
                    onChange={(warehouse: Warehouse) => {
                      onSetAllField({ warehouse, warehouseId: warehouse?.id });
                    }}
                  />
                </SkeletonComponent>
              </Grid>
              <Grid item xs={4}>
                <AutoSuggest
                  disableCloseOnSelect
                  label={translate("team")}
                  placeholder={fields?.teams?.length ? "" : translate("select")}
                  ignoreValue
                  disabled={isView}
                  onChange={(teams: Fleet["teams"]) => {
                    onSetField("teams", teams);
                  }}
                  defaultValue={fields?.teams || []}
                  multiple
                  options={teamList || []}
                  getOptionLabel={(team?: any) => (team && team.name ? team.name : "")}
                  getOptionSelected={(option: any, value: any) => {
                    return option.id === value.id;
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <Input
                  label={translate("rate")}
                  value={fields?.ratePerHour}
                  onChange={(event) => {
                    onSetField("ratePerHour", event.target.value);
                  }}
                  onBlur={(event) => {
                    onSetField(
                      "ratePerHour",
                      formatCurrencyToInput(currencyToNumber(event.target.value))
                    );
                  }}
                  required
                  placeholder={"0.00"}
                  leftIcon={<IconWrapper>$</IconWrapper>}
                  rightIcon={<IconWrapper>/{translate("hour")}</IconWrapper>}
                  inputTextAlign={"start"}
                  error={!!getErrorByField("ratePerHour")}
                  errorMessage={getErrorByField("ratePerHour")}
                />
              </Grid>
              <Grid item xs={4}>
                <Select
                  label={translate("belongsTo")}
                  value={fields?.ownership}
                  onChange={(event: any) => onChangeField("ownership", event)}
                  disabled={isView}
                  required
                  error={!!getErrorByField("ownership")}
                  errorMessage={getErrorByField("ownership")}
                >
                  {(ownershipOptions || []).map((option, index) => (
                    <MenuItem value={option.value} key={index}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>

              {[EquipmentOwnership.SUBCONTRACTOR].includes(
                fields.ownership as EquipmentOwnership
              ) && (
                <Grid item xs={4}>
                  <SkeletonComponent isLoading={loadingSubcontractors}>
                    <Select
                      label={translate("subcontractor")}
                      value={fields.subcontractorId}
                      onChange={(event: any) => onChangeField("subcontractorId", event)}
                      required
                      error={!!getErrorByField("subcontractorId")}
                      errorMessage={getErrorByField("subcontractorId")}
                      disabled={isView}
                    >
                      {(subcontractorList || []).map((subcontractor, index) => (
                        <MenuItem value={subcontractor?.id} key={index}>
                          {subcontractor?.businessName}
                        </MenuItem>
                      ))}
                    </Select>
                  </SkeletonComponent>
                </Grid>
              )}
              <Grid item xs={12}>
                <Checkbox
                  control={{
                    "data-cy": "isLoadable",
                  }}
                  disabled={isView}
                  checked={fields.isLoadable}
                  label={translate("enableThisFleetAsAStorageBox")}
                  onChange={(_, checked) => onSetField("isLoadable", checked)}
                  tooltipText={translate(
                    "enablingThisAllowsTheUserToChooseTheVehicleForStorageMovementOrTransshipmentAndIndicateWhenItemsArePlacedInsideLikeAStorageBox"
                  )}
                  tooltipPlacement={TooltipPlacements.TOP_END}
                />
              </Grid>
            </RightContentWrapper>
          </RightContentContainer>
        </>
      );
    }
    if (fields?.equipmentType === EquipmentType.GENERAL) {
      return (
        <>
          <RightContentContainer>
            <Title>{translate("generalDetails")}</Title>
            <RightContentWrapper item xs={9} container direction="row" spacing={3}>
              <Grid item xs={4}>
                <Input
                  label={translate("description")}
                  value={fields.description}
                  onChange={(event) => onChangeField("description", event)}
                  required
                  disabled={isView}
                  error={!!getErrorByField("description")}
                  errorMessage={getErrorByField("description")}
                />
              </Grid>
              <Grid item xs={4}>
                <Input
                  label={translate("make")}
                  value={fields.make}
                  onChange={(event) => onChangeField("make", event)}
                  disabled={isView}
                />
              </Grid>
              <Grid item xs={4}>
                <Select
                  label={translate("status")}
                  value={fields.status}
                  onChange={(event: any) => onChangeField("status", event)}
                  disabled={isView}
                  required
                  error={!!getErrorByField("status")}
                  errorMessage={getErrorByField("status")}
                >
                  {[Status.ACTIVE, Status.INACTIVE].map((option, index) => (
                    <MenuItem value={option} key={index}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid item xs={4}>
                <Input
                  label={translate("model")}
                  value={fields.model}
                  onChange={(event) => onChangeField("model", event)}
                  disabled={isView}
                />
              </Grid>
              <Grid item xs={4}>
                <Input
                  label={translate("year")}
                  value={fields.year}
                  disabled={isView}
                  onChange={(event) => onChangeField("year", event)}
                />
              </Grid>
              <Grid item xs={4}>
                <Input
                  label={translate("condition")}
                  value={fields.condition}
                  disabled={isView}
                  onChange={(event) => onChangeField("condition", event)}
                />
              </Grid>
              <Grid item xs={4}>
                <Input
                  label={translate("capacity")}
                  value={fields.capacityM3}
                  onChange={(event) => onChangeField("capacityM3", event)}
                  rightIcon={<span>{getMeasurement()?.code}</span>}
                  disabled={isView}
                />
              </Grid>
              <Grid item xs={4}>
                <SkeletonComponent isLoading={loadingBranches}>
                  <AutoSuggest
                    required
                    disableCloseOnSelect
                    label={translate("branch")}
                    placeholder={fields?.branches?.length ? "" : translate("select")}
                    ignoreValue
                    disabled={isView}
                    onOpen={() => {
                      temporaryBranchesSelected = fields?.branches || [];
                    }}
                    onChange={(branches: Branch[]) => {
                      temporaryBranchesSelected = branches;
                      onSetField("branches", temporaryBranchesSelected);
                    }}
                    onClose={() => {
                      onSetField("branches", temporaryBranchesSelected);
                      temporaryBranchesSelected = [];
                    }}
                    defaultValue={fields?.branches || []}
                    error={!!getErrorByField("branches")}
                    errorMessage={getErrorByField("branches")}
                    multiple
                    options={branchList || []}
                    getOptionLabel={(branch?: Branch) => (branch && branch.name ? branch.name : "")}
                    getOptionSelected={(option: Branch, value: Branch) => {
                      return option.id === value.id;
                    }}
                    renderOption={(
                      props: React.HTMLAttributes<any>,
                      branch: Branch,
                      { selected }: { selected: boolean }
                    ) => (
                      <BranchOption {...props}>
                        <Checkbox checked={selected} />
                        <BranchOptionText>{branch.name}</BranchOptionText>
                      </BranchOption>
                    )}
                  />
                </SkeletonComponent>
              </Grid>
              <Grid item xs={4}>
                <SkeletonComponent isLoading={loadingWarehouses}>
                  <AutoSuggest
                    dataCy="warehouseAutosuggest"
                    label={translate("warehouse")}
                    placeholder={translate("searchForWarehouse")}
                    required
                    error={!!getErrorByField("warehouseId")}
                    errorMessage={getErrorByField("warehouseId")}
                    inputValue={fields?.warehouse?.name || ""}
                    disabled={isView}
                    getOptionLabel={(warehouse?: Warehouse) =>
                      warehouse && warehouse.name ? warehouse.name : ""
                    }
                    getOptionSelected={(option?: Warehouse, value?: Warehouse) =>
                      option?.id === value?.id
                    }
                    options={warehouseList || []}
                    onChange={(warehouse: Warehouse) => {
                      onSetAllField({ warehouse, warehouseId: warehouse?.id });
                    }}
                  />
                </SkeletonComponent>
              </Grid>
              <Grid item xs={4}>
                <SkeletonComponent isLoading={loadingTeams}>
                  <AutoSuggest
                    disableCloseOnSelect
                    label={translate("team")}
                    disabled={isView}
                    placeholder={fields?.teams?.length ? "" : translate("select")}
                    ignoreValue
                    onChange={(teams: Fleet["teams"]) => {
                      onSetField("teams", teams);
                    }}
                    defaultValue={fields?.teams || []}
                    multiple
                    options={teamList || []}
                    getOptionLabel={(team?: any) => (team && team.name ? team.name : "")}
                    getOptionSelected={(option: any, value: any) => {
                      return option.id === value.id;
                    }}
                  />
                </SkeletonComponent>
              </Grid>
              <Grid item xs={4}>
                <Input
                  label={translate("rate")}
                  value={fields?.ratePerHour}
                  onChange={(event) => {
                    onSetField("ratePerHour", event.target.value);
                  }}
                  onBlur={(event) => {
                    onSetField(
                      "ratePerHour",
                      formatCurrencyToInput(currencyToNumber(event.target.value))
                    );
                  }}
                  required
                  placeholder={"0.00"}
                  leftIcon={<IconWrapper>$</IconWrapper>}
                  rightIcon={<IconWrapper>/{translate("hour")}</IconWrapper>}
                  inputTextAlign={"start"}
                  error={!!getErrorByField("ratePerHour")}
                  errorMessage={getErrorByField("ratePerHour")}
                />
              </Grid>
              <Grid item xs={4}>
                <Select
                  label={translate("belongsTo")}
                  value={fields?.ownership}
                  onChange={(event: any) => onChangeField("ownership", event)}
                  disabled={isView}
                  required
                  error={!!getErrorByField("ownership")}
                  errorMessage={getErrorByField("ownership")}
                >
                  {(ownershipOptions || []).map((option, index) => (
                    <MenuItem value={option.value} key={index}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              {[EquipmentOwnership.SUBCONTRACTOR].includes(
                fields.ownership as EquipmentOwnership
              ) && (
                <Grid item xs={4}>
                  <SkeletonComponent isLoading={loadingSubcontractors}>
                    <Select
                      label={translate("subcontractor")}
                      value={fields.subcontractorId}
                      onChange={(event: any) => onChangeField("subcontractorId", event)}
                      required
                      error={!!getErrorByField("subcontractorId")}
                      errorMessage={getErrorByField("subcontractorId")}
                      disabled={isView}
                    >
                      {(subcontractorList || []).map((subcontractor, index) => (
                        <MenuItem value={subcontractor?.id} key={index}>
                          {subcontractor?.businessName}
                        </MenuItem>
                      ))}
                    </Select>
                  </SkeletonComponent>
                </Grid>
              )}
            </RightContentWrapper>
          </RightContentContainer>
        </>
      );
    }
  };

  useEffect(() => {
    if (!isBefore(new Date(fields?.purchaseDate!), new Date(fields?.soldDate!)))
      setEquipment((prev) => ({ ...prev, soldDate: undefined }));
  }, [fields?.purchaseDate, fields?.soldDate]);

  useEffect(() => {
    if (!isBefore(new Date(fields?.lastInspectionDate!), new Date(fields?.nextInspectionDate!)))
      setEquipment((prev) => ({ ...prev, nextInspectionDate: undefined }));
  }, [fields?.lastInspectionDate, fields?.nextInspectionDate]);

  // const getDateValue = (date: string | Date) => {
  //   if (typeof date === "string") {
  //     if (date.includes("Z")) return format(new Date(date.split("Z")[0]), "yyyy-MM-dd");

  //     return zonedTimeToUtc(
  //       new Date(date),
  //       Intl.DateTimeFormat().resolvedOptions().timeZone
  //     ).toISOString();
  //   }
  // };

  return (
    <Grid container direction="column">
      <DetailsContainer>
        {renderLeftContentByEquipmentTypeSelected()}
        {renderDetailsContentByEquipmentTypeSelected()}
      </DetailsContainer>
      {/* <Divider />
      <DatesContainer>
        <ItemWrapper>
          <Title>{translate("dates")}</Title>
        </ItemWrapper>
        <ItemWrapper>
          <InputWrapper>
            <Input
              label={translate("purchaseDate")}
              value={fields?.purchaseDate ? getDateValue(fields?.purchaseDate) : ""}
              onChange={(event) => {
                setEquipment((prev) => ({
                  ...prev,
                  purchaseDate: utcToZonedTime(
                    event.target.value,
                    Intl.DateTimeFormat().resolvedOptions().timeZone
                  ),
                }));
              }}
              type="date"
            />
          </InputWrapper>
          <InputWrapper>
            <Input
              label={translate("soldDate")}
              value={fields?.soldDate ? getDateValue(fields?.soldDate) : ""}
              onChange={(event) => {
                setEquipment((prev) => ({
                  ...prev,
                  soldDate: utcToZonedTime(
                    event.target.value,
                    Intl.DateTimeFormat().resolvedOptions().timeZone
                  ),
                }));
              }}
              type="date"
            />
          </InputWrapper>
        </ItemWrapper>
        <ItemWrapper>
          <InputWrapper>
            <Input
              label={translate("lastInspectionDate")}
              value={fields?.lastInspectionDate ? getDateValue(fields?.lastInspectionDate) : ""}
              onChange={(event) => {
                setEquipment((prev) => ({
                  ...prev,
                  lastInspectionDate: utcToZonedTime(
                    event.target.value,
                    Intl.DateTimeFormat().resolvedOptions().timeZone
                  ),
                }));
              }}
              type="date"
            />
          </InputWrapper>
          <InputWrapper>
            <Input
              label={translate("nextInspectionDate")}
              value={fields?.nextInspectionDate ? getDateValue(fields?.nextInspectionDate) : ""}
              onChange={(event) => {
                setEquipment((prev) => ({
                  ...prev,
                  nextInspectionDate: utcToZonedTime(
                    event.target.value,
                    Intl.DateTimeFormat().resolvedOptions().timeZone
                  ),
                }));
              }}
              type="date"
            />
          </InputWrapper>
        </ItemWrapper>
      </DatesContainer> */}
    </Grid>
  );
};
